"use client"
import { useState, useEffect } from "react";
import ProfessionalsSection from "./ProfessionalsSection";
import ProjectsSection from "./ProjectsSection";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";

export default function MainContent({ toggleSidebar, isSidebarOpen, showSections = true }) {
  const [currentAd, setCurrentAd] = useState(0);
  const [ads, setAds] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    // Fetch ads from the database
    axios.get("http://localhost:5000/ads")
      .then((response) => setAds(response.data))
      .catch((error) => console.error("Error fetching ads:", error));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAd((prevAd) => (ads.length > 0 ? (prevAd + 1) % ads.length : 0));
    }, 5000);

    return () => clearInterval(interval);
  }, [ads]);

  return (
    <main style={{
      flexGrow: 1,
      transition: 'margin-left 0.3s ease',
      marginLeft: isSidebarOpen ? '384px' : '0',
    }}>      
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
        
        {/* Hero Ad Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'relative',
            borderRadius: '24px',
            overflow: 'hidden',
            padding: '56px 48px',
            margin: '32px 0 48px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-glass)',
            backdropFilter: 'var(--card-backdrop)',
            WebkitBackdropFilter: 'var(--card-backdrop)',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          {/* Animated gradient accent bar */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, var(--accent-cyan), var(--accent-violet), var(--accent-magenta), var(--accent-cyan))',
            backgroundSize: '300% 100%',
            animation: 'gradient-shift 4s ease infinite',
          }} />

          {/* Floating decoration */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '40px',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            border: '1px solid rgba(var(--accent-primary-rgb), 0.1)',
            animation: 'spin-slow 25s linear infinite',
            pointerEvents: 'none',
          }}>
            <div style={{
              position: 'absolute',
              top: '-3px',
              left: '50%',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: 'var(--accent-cyan)',
              opacity: 0.5,
            }} />
          </div>

          {ads.length > 0 && ads[currentAd] ? (
            <motion.div
              key={currentAd}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{
                display: 'inline-block',
                padding: '4px 14px',
                borderRadius: '9999px',
                background: 'rgba(var(--accent-primary-rgb), 0.1)',
                border: '1px solid rgba(var(--accent-primary-rgb), 0.2)',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--accent-cyan)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '16px',
              }}>
                {t("featured")}
              </div>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: '12px',
                lineHeight: 1.2,
              }}>{ads[currentAd].title}</h2>
              <p style={{
                fontSize: '1.05rem',
                color: 'var(--text-secondary)',
                maxWidth: '600px',
                lineHeight: 1.7,
              }}>{ads[currentAd].description}</p>

              {/* Ad dots indicator */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginTop: '24px',
              }}>
                {ads.map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: i === currentAd ? '24px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      background: i === currentAd ? 'var(--gradient-primary)' : 'var(--border-glass)',
                      transition: 'all 0.4s ease',
                      cursor: 'pointer',
                    }}
                    onClick={() => setCurrentAd(i)}
                  />
                ))}
              </div>
            </motion.div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--text-muted)',
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: '2px solid var(--accent-cyan)',
                borderTopColor: 'transparent',
                animation: 'spin-slow 1s linear infinite',
              }} />
              {t("loadingHighlights")}
            </div>
          )}
        </motion.section>

        {/* Welcome Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            marginBottom: '64px',
            padding: '48px',
            borderRadius: '24px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-glass)',
            backdropFilter: 'var(--card-backdrop)',
            WebkitBackdropFilter: 'var(--card-backdrop)',
            boxShadow: 'var(--shadow-sm)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Corner decoration */}
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: '-30px',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(var(--accent-primary-rgb), 0.06), transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{
            display: 'inline-block',
            padding: '4px 14px',
            borderRadius: '9999px',
            background: 'rgba(124, 58, 237, 0.1)',
            border: '1px solid rgba(124, 58, 237, 0.2)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: '#a78bfa',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '20px',
          }}>
            {t("about")}
          </div>

          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 700,
            marginBottom: '20px',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {t("welcomeToDevGenius")}
          </h2>
          <p style={{
            fontSize: '1.05rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            marginBottom: '16px',
            maxWidth: '800px',
          }}>
            {t("aboutText1")}
          </p>
          <p style={{
            fontSize: '1.05rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            maxWidth: '800px',
          }}>
            {t("aboutText2")}
          </p>

          {/* Feature pills */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginTop: '28px',
          }}>
            {[t('featureSmartMatching'), t('featureVerifiedProfiles'), t('featureRealtimeCollab'), t('featureProjectTracking')].map((feature, i) => (
              <motion.span
                key={feature}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                style={{
                  padding: '8px 18px',
                  borderRadius: '9999px',
                  background: 'var(--bg-glass)',
                  border: '1px solid var(--border-subtle)',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                }}
              >
                {feature}
              </motion.span>
            ))}
          </div>
        </motion.section>

        {showSections && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <ProfessionalsSection />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <ProjectsSection latestProjects={4} />
            </motion.div>
          </>
        )}
        
      </div>
    </main>
  );
}